import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getTelegramWebApp } from "./telegram";

export type Locale = "ro" | "ru";

interface TranslationMap {
  loading: string;
  save: string;
  openInTelegramTitle: string;
  openInTelegramMessage: string;
  sessionNeededTitle: string;
  sessionNeededMessage: string;
  openBot: string;
  welcomeToMesterin: string;
  howUsingApp: string;
  needService: string;
  needServiceHint: string;
  offerServices: string;
  offerServicesHint: string;
  welcomeBack: string;
  guest: string;
  yourLeads: string;
  noLeadsYet: string;
  newRequest: string;
  newRequestHint: string;
  requestPageTitle: string;
  requestPageSubtitle: string;
  requestStepDescription: string;
  requestStepDetails: string;
  requestStepMode: string;
  requestStepReview: string;
  category: string;
  city: string;
  cityPlaceholder: string;
  urgency: string;
  urgencyFlexible: string;
  urgencyToday: string;
  urgencyUrgent: string;
  mode: string;
  modeBrowse: string;
  modeBrowseHint: string;
  modeBroadcast: string;
  modeBroadcastHint: string;
  modeOperator: string;
  modeOperatorHint: string;
  scheduledFor: string;
  schedulePlaceholder: string;
  description: string;
  descriptionPlaceholder: string;
  back: string;
  submitRequest: string;
  saveDraft: string;
  savedDraft: string;
  lastDraft: string;
  requestCreated: string;
  requestStatus: string;
  requestDetails: string;
  selectedMode: string;
  foundProviders: string;
  searchingProviders: string;
  searchCompleted: string;
  timeRemaining: string;
  noMatches: string;
  viewProfile: string;
  openTelegram: string;
  callPhone: string;
  providerProfileTitle: string;
  providerCategories: string;
  verificationLevel: string;
  reviewsCount: string;
  noPortfolioYet: string;
  handymanProfileTitle: string;
  handymanProfileSubtitle: string;
  displayName: string;
  phone: string;
  avatarUrl: string;
  headline: string;
  bio: string;
  yearsExperience: string;
  serviceRadius: string;
  availability: string;
  zones: string;
  addZone: string;
  profileSaved: string;
  openProfileSettings: string;
  openLeads: string;
  leadsTitle: string;
  clientProfile: string;
  noLeadsShort: string;
  editProfile: string;
  profilePublicNote: string;
  requestTakenNotice: string;
  acceptLead: string;
  declineLead: string;
  leadStatusPending: string;
  leadStatusAccepted: string;
  leadStatusDeclined: string;
  leadStatusWithdrawn: string;
  matchScore: string;
  requestCategory: string;
  clientRating: string;
  recentRequests: string;
  noRequestsShort: string;
  currentJobs: string;
  noJobsShort: string;
  openRequest: string;
  assignedHandyman: string;
  scheduledAt: string;
  chooseCity: string;
  anytime: string;
  managePortfolio: string;
  manageVerification: string;
  portfolioTitle: string;
  portfolioSubtitle: string;
  portfolioCaseTitle: string;
  portfolioCaseDescription: string;
  portfolioLocation: string;
  completedAt: string;
  uploadPhotos: string;
  saveCase: string;
  deleting: string;
  noPortfolioCases: string;
  uploadError: string;
  portfolioSaved: string;
  deleteCase: string;
  addCaseHint: string;
  verificationTitle: string;
  verificationSubtitle: string;
  verificationIdentity: string;
  verificationProfessional: string;
  verificationNotes: string;
  verificationNotesPlaceholder: string;
  verificationFiles: string;
  uploadDocuments: string;
  submitVerification: string;
  noVerificationSubmissions: string;
  verificationSubmitted: string;
  verificationStatusPending: string;
  verificationStatusApproved: string;
  verificationStatusRejected: string;
  verificationStatusCancelled: string;
  categories: {
    plumber: string;
    electrician: string;
    applianceRepair: string;
    cleaning: string;
    moving: string;
    taxi: string;
    other: string;
  };
  requestStatusLabels: {
    draft: string;
    pending: string;
    matching: string;
    assigned: string;
    in_progress: string;
    completed: string;
    cancelled: string;
    expired: string;
  };
  handymenTitle: string;
  filterByCategory: string;
  allCategories: string;
  noHandymenFound: string;
  browseHandymen: string;
  becomeHandyman: string;
  switchToClient: string;
  switchToHandyman: string;
  searchHandymen: string;
  availabilityLabels: {
    available: string;
    busy: string;
    offline: string;
    onVacation: string;
  };
  verificationLabels: {
    unverified: string;
    identityVerified: string;
    proVerified: string;
    trustedPro: string;
  };
}

const STORAGE_KEY = "mestergo_locale";

const translations: Record<Locale, TranslationMap> = {
  ro: {
    loading: "Se încarcă...",
    save: "Salvează",
    openInTelegramTitle: "Deschide În Telegram",
    openInTelegramMessage:
      "Această Mini App trebuie deschisă din meniul botului Telegram sau din butonul Web App.",
    sessionNeededTitle: "Sesiune Telegram Necesară",
    sessionNeededMessage:
      "Sesiunea Telegram nu a putut fi verificată. Redeschide Mini App din chatul botului și încearcă din nou.",
    openBot: "Deschide @mesterin_md_bot",
    switchToClient: "Mod client",
    switchToHandyman: "Mod meșter",
    searchHandymen: "Caută după nume, specialitate...",
    welcomeToMesterin: "Bine ai venit la Mesterin",
    howUsingApp: "Cum vrei să folosești aplicația?",
    needService: "Am nevoie de un serviciu",
    needServiceHint: "Găsește instalatori, electricieni, curățenie",
    offerServices: "Ofer servicii",
    offerServicesHint: "Primește cereri și dezvoltă-ți afacerea",
    welcomeBack: "Bine ai revenit,",
    guest: "Oaspete",
    yourLeads: "Cererile tale",
    noLeadsYet: "Încă nu ai cereri noi. Completează profilul ca să începi să primești lucrări.",
    newRequest: "Cerere nouă",
    newRequestHint: "Descrie de ce ai nevoie",
    requestPageTitle: "Cerere nouă",
    requestPageSubtitle: "Completează pașii și trimite cererea către meșterii potriviți.",
    requestStepDescription: "1. Problema",
    requestStepDetails: "2. Detalii",
    requestStepMode: "3. Mod de căutare",
    requestStepReview: "4. Confirmare",
    category: "Categorie",
    city: "Oraș",
    cityPlaceholder: "Chișinău",
    urgency: "Urgență",
    urgencyFlexible: "Flexibil",
    urgencyToday: "Astăzi",
    urgencyUrgent: "Urgent",
    mode: "Mod",
    modeBrowse: "Arată-mi meșteri",
    modeBrowseHint: "Vezi și alegi manual prestatorul potrivit.",
    modeBroadcast: "Primește oferte",
    modeBroadcastHint: "Trimitem cererea către meșteri disponibili.",
    modeOperator: "Ajutor operator",
    modeOperatorHint: "Cererea intră în coada de operator pentru repartizare manuală.",
    scheduledFor: "Programare",
    schedulePlaceholder: "2026-04-12T18:00",
    description: "Descriere",
    descriptionPlaceholder:
      "Exemplu: am nevoie de un electrician în Chișinău pentru a schimba prizele și a verifica tabloul.",
    back: "Înapoi",
    submitRequest: "Trimite cererea",
    saveDraft: "Salvează draftul",
    savedDraft: "Draft salvat local. Poți reveni și continua oricând.",
    lastDraft: "Ultimul draft",
    requestCreated: "Cererea a fost creată.",
    requestStatus: "Status",
    requestDetails: "Detalii cerere",
    selectedMode: "Mod selectat",
    foundProviders: "Meșteri găsiți",
    searchingProviders: "Căutăm meșteri potriviți pentru cererea ta.",
    searchCompleted: "Potrivirea este gata. Poți vedea prestatorii recomandați mai jos.",
    timeRemaining: "Timp rămas",
    noMatches: "Încă nu există meșteri potriviți. Încearcă să extinzi categoria sau zona.",
    viewProfile: "Vezi profilul",
    openTelegram: "Scrie pe Telegram",
    callPhone: "Sună",
    providerProfileTitle: "Profil meșter",
    providerCategories: "Categorii",
    verificationLevel: "Verificare",
    reviewsCount: "Recenzii",
    noPortfolioYet: "Portofoliul nu are încă proiecte publice.",
    handymanProfileTitle: "Profil prestator",
    handymanProfileSubtitle: "Configurează datele publice, categoriile și zonele de lucru.",
    displayName: "Nume afișat",
    phone: "Telefon",
    avatarUrl: "Link foto profil",
    headline: "Titlu profil",
    bio: "Descriere",
    yearsExperience: "Ani experiență",
    serviceRadius: "Rază servicii (km)",
    availability: "Disponibilitate",
    zones: "Zone de lucru",
    addZone: "Adaugă zonă",
    profileSaved: "Profilul a fost salvat.",
    openProfileSettings: "Editează profilul",
    openLeads: "Vezi cererile",
    leadsTitle: "Inbox cereri",
    clientProfile: "Profil client",
    noLeadsShort: "Momentan nu ai cereri.",
    editProfile: "Editează profilul",
    profilePublicNote: "Profilul public și categoriile tale sunt folosite pentru potrivirea cererilor.",
    requestTakenNotice: "Cererea a fost acceptată de alt meșter și a fost scoasă din inbox.",
    acceptLead: "Acceptă",
    declineLead: "Refuză",
    leadStatusPending: "În așteptare",
    leadStatusAccepted: "Acceptată",
    leadStatusDeclined: "Refuzată",
    leadStatusWithdrawn: "Preluată de alt meșter",
    matchScore: "Scor potrivire",
    requestCategory: "Categorie cerere",
    clientRating: "Rating client",
    recentRequests: "Cererile mele",
    noRequestsShort: "Nu ai încă cereri trimise.",
    currentJobs: "Lucrări active",
    noJobsShort: "Nu ai încă lucrări active.",
    openRequest: "Deschide cererea",
    assignedHandyman: "Meșter atribuit",
    scheduledAt: "Programat pentru",
    chooseCity: "Alege orașul",
    anytime: "Oricând",
    managePortfolio: "Gestionează portofoliul",
    manageVerification: "Trimite verificarea",
    portfolioTitle: "Portofoliu",
    portfolioSubtitle: "Adaugă lucrări reale cu poze și descrieri clare.",
    portfolioCaseTitle: "Titlul lucrării",
    portfolioCaseDescription: "Descrierea lucrării",
    portfolioLocation: "Locație",
    completedAt: "Finalizat la",
    uploadPhotos: "Încarcă poze",
    saveCase: "Salvează lucrarea",
    deleting: "Se șterge...",
    noPortfolioCases: "Nu ai încă lucrări în portofoliu.",
    uploadError: "Încărcarea imaginilor a eșuat.",
    portfolioSaved: "Lucrarea a fost salvată.",
    deleteCase: "Șterge lucrarea",
    addCaseHint: "Minimum 3 imagini și cel puțin o lucrare cresc șansele de potrivire.",
    verificationTitle: "Verificare profil",
    verificationSubtitle: "Încarcă documentele necesare pentru identitate sau verificare profesională.",
    verificationIdentity: "Verificare identitate",
    verificationProfessional: "Verificare profesională",
    verificationNotes: "Note",
    verificationNotesPlaceholder: "Exemplu: trimit buletinul și poze cu lucrările recente.",
    verificationFiles: "Fișiere verificare",
    uploadDocuments: "Încarcă documente",
    submitVerification: "Trimite pentru verificare",
    noVerificationSubmissions: "Nu ai încă trimiteri de verificare.",
    verificationSubmitted: "Verificarea a fost trimisă.",
    verificationStatusPending: "În așteptare",
    verificationStatusApproved: "Aprobată",
    verificationStatusRejected: "Respinsă",
    verificationStatusCancelled: "Anulată",
    categories: {
      plumber: "Instalator",
      electrician: "Electrician",
      applianceRepair: "Reparații electrocasnice",
      cleaning: "Curățenie",
      moving: "Mutare",
      taxi: "Taxi",
      other: "Altceva",
    },
    requestStatusLabels: {
      draft: "Ciornă",
      pending: "În așteptare",
      matching: "Căutăm meșteri",
      assigned: "Atribuit",
      in_progress: "În lucru",
      completed: "Finalizat",
      cancelled: "Anulat",
      expired: "Expirat",
    },
    handymenTitle: "Meșteri disponibili",
    filterByCategory: "Filtrează după categorie",
    allCategories: "Toate categoriile",
    noHandymenFound: "Nu am găsit meșteri în această categorie.",
    browseHandymen: "Caută meșteri",
    becomeHandyman: "Devino meșter",
    availabilityLabels: {
      available: "Disponibil",
      busy: "Ocupat",
      offline: "Offline",
      onVacation: "În vacanță",
    },
    verificationLabels: {
      unverified: "Neverificat",
      identityVerified: "Identitate verificată",
      proVerified: "Profesional verificat",
      trustedPro: "Prestator de încredere",
    },
  },
  ru: {
    loading: "Загрузка...",
    save: "Сохранить",
    openInTelegramTitle: "Откройте В Telegram",
    openInTelegramMessage:
      "Это Mini App нужно открывать из меню бота Telegram или через кнопку Web App.",
    sessionNeededTitle: "Нужна Сессия Telegram",
    sessionNeededMessage:
      "Не удалось проверить сессию Telegram. Откройте Mini App снова из чата бота и попробуйте ещё раз.",
    openBot: "Открыть @mesterin_md_bot",
    switchToClient: "Режим клиента",
    switchToHandyman: "Режим мастера",
    searchHandymen: "Поиск по имени, специальности...",
    welcomeToMesterin: "Добро пожаловать в Mesterin",
    howUsingApp: "Как вы хотите использовать приложение?",
    needService: "Мне нужен сервис",
    needServiceHint: "Найдите сантехника, электрика, уборку",
    offerServices: "Я оказываю услуги",
    offerServicesHint: "Получайте заявки и развивайте бизнес",
    welcomeBack: "С возвращением,",
    guest: "Гость",
    yourLeads: "Ваши заявки",
    noLeadsYet: "Пока новых заявок нет. Заполните профиль, чтобы начать получать заказы.",
    newRequest: "Новая заявка",
    newRequestHint: "Опишите, что вам нужно",
    requestPageTitle: "Новая заявка",
    requestPageSubtitle: "Пройдите шаги и отправьте заявку подходящим мастерам.",
    requestStepDescription: "1. Проблема",
    requestStepDetails: "2. Детали",
    requestStepMode: "3. Режим поиска",
    requestStepReview: "4. Подтверждение",
    category: "Категория",
    city: "Город",
    cityPlaceholder: "Кишинёв",
    urgency: "Срочность",
    urgencyFlexible: "Гибко",
    urgencyToday: "Сегодня",
    urgencyUrgent: "Срочно",
    mode: "Режим",
    modeBrowse: "Показать мастеров",
    modeBrowseHint: "Смотрите и выбирайте подходящего специалиста вручную.",
    modeBroadcast: "Получить отклики",
    modeBroadcastHint: "Мы отправим заявку доступным мастерам.",
    modeOperator: "Помощь оператора",
    modeOperatorHint: "Заявка попадёт в очередь оператора для ручного назначения.",
    scheduledFor: "Когда нужно",
    schedulePlaceholder: "2026-04-12T18:00",
    description: "Описание",
    descriptionPlaceholder:
      "Например: нужен электрик в Кишинёве, чтобы заменить розетки и проверить щиток.",
    back: "Назад",
    submitRequest: "Отправить заявку",
    saveDraft: "Сохранить черновик",
    savedDraft: "Черновик сохранён локально. Вы можете вернуться к нему позже.",
    lastDraft: "Последний черновик",
    requestCreated: "Заявка создана.",
    requestStatus: "Статус",
    requestDetails: "Детали заявки",
    selectedMode: "Выбранный режим",
    foundProviders: "Найденные мастера",
    searchingProviders: "Ищем мастеров, подходящих под вашу заявку.",
    searchCompleted: "Подбор завершён. Ниже можно посмотреть рекомендованных специалистов.",
    timeRemaining: "Осталось времени",
    noMatches: "Подходящих мастеров пока нет. Попробуйте расширить категорию или район.",
    viewProfile: "Открыть профиль",
    openTelegram: "Написать в Telegram",
    callPhone: "Позвонить",
    providerProfileTitle: "Профиль мастера",
    providerCategories: "Категории",
    verificationLevel: "Проверка",
    reviewsCount: "Отзывы",
    noPortfolioYet: "В портфолио пока нет публичных проектов.",
    handymanProfileTitle: "Профиль мастера",
    handymanProfileSubtitle: "Настройте публичные данные, категории и зоны работы.",
    displayName: "Отображаемое имя",
    phone: "Телефон",
    avatarUrl: "Ссылка на фото профиля",
    headline: "Заголовок профиля",
    bio: "Описание",
    yearsExperience: "Лет опыта",
    serviceRadius: "Радиус услуг (км)",
    availability: "Доступность",
    zones: "Зоны работы",
    addZone: "Добавить зону",
    profileSaved: "Профиль сохранён.",
    openProfileSettings: "Редактировать профиль",
    openLeads: "Открыть заявки",
    leadsTitle: "Входящие заявки",
    clientProfile: "Профиль клиента",
    noLeadsShort: "Пока заявок нет.",
    editProfile: "Редактировать профиль",
    profilePublicNote: "Ваш публичный профиль и категории используются для подбора заявок.",
    requestTakenNotice: "Заявку уже принял другой мастер, она убрана из входящих.",
    acceptLead: "Принять",
    declineLead: "Отклонить",
    leadStatusPending: "Ожидает ответа",
    leadStatusAccepted: "Принята",
    leadStatusDeclined: "Отклонена",
    leadStatusWithdrawn: "Уже забрал другой мастер",
    matchScore: "Оценка совпадения",
    requestCategory: "Категория заявки",
    clientRating: "Рейтинг клиента",
    recentRequests: "Мои заявки",
    noRequestsShort: "У вас пока нет отправленных заявок.",
    currentJobs: "Активные работы",
    noJobsShort: "Пока нет активных работ.",
    openRequest: "Открыть заявку",
    assignedHandyman: "Назначенный мастер",
    scheduledAt: "Запланировано на",
    chooseCity: "Выберите город",
    anytime: "В любое время",
    managePortfolio: "Управлять портфолио",
    manageVerification: "Отправить проверку",
    portfolioTitle: "Портфолио",
    portfolioSubtitle: "Добавляйте реальные работы с фото и понятным описанием.",
    portfolioCaseTitle: "Название работы",
    portfolioCaseDescription: "Описание работы",
    portfolioLocation: "Локация",
    completedAt: "Завершено",
    uploadPhotos: "Загрузить фото",
    saveCase: "Сохранить работу",
    deleting: "Удаление...",
    noPortfolioCases: "У вас пока нет работ в портфолио.",
    uploadError: "Не удалось загрузить изображения.",
    portfolioSaved: "Работа сохранена.",
    deleteCase: "Удалить работу",
    addCaseHint: "Минимум 3 фото и хотя бы одна работа заметно улучшают профиль.",
    verificationTitle: "Проверка профиля",
    verificationSubtitle: "Загрузите документы для подтверждения личности или профессионального статуса.",
    verificationIdentity: "Проверка личности",
    verificationProfessional: "Профессиональная проверка",
    verificationNotes: "Примечания",
    verificationNotesPlaceholder: "Например: прикладываю паспорт и фото недавних работ.",
    verificationFiles: "Файлы для проверки",
    uploadDocuments: "Загрузить документы",
    submitVerification: "Отправить на проверку",
    noVerificationSubmissions: "У вас пока нет отправленных проверок.",
    verificationSubmitted: "Проверка отправлена.",
    verificationStatusPending: "На рассмотрении",
    verificationStatusApproved: "Одобрена",
    verificationStatusRejected: "Отклонена",
    verificationStatusCancelled: "Отменена",
    categories: {
      plumber: "Сантехник",
      electrician: "Электрик",
      applianceRepair: "Ремонт техники",
      cleaning: "Уборка",
      moving: "Переезд",
      taxi: "Такси",
      other: "Другое",
    },
    requestStatusLabels: {
      draft: "Черновик",
      pending: "Ожидает",
      matching: "Ищем мастеров",
      assigned: "Назначен",
      in_progress: "В работе",
      completed: "Завершён",
      cancelled: "Отменён",
      expired: "Истёк",
    },
    handymenTitle: "Доступные мастера",
    filterByCategory: "Фильтр по категории",
    allCategories: "Все категории",
    noHandymenFound: "Мастера в этой категории не найдены.",
    browseHandymen: "Найти мастера",
    becomeHandyman: "Стать мастером",
    availabilityLabels: {
      available: "Доступен",
      busy: "Занят",
      offline: "Оффлайн",
      onVacation: "В отпуске",
    },
    verificationLabels: {
      unverified: "Не проверен",
      identityVerified: "Личность подтверждена",
      proVerified: "Профи подтверждён",
      trustedPro: "Надёжный мастер",
    },
  },
};

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationMap;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function detectInitialLocale(): Locale {
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "ro" || stored === "ru") return stored;
  }

  const telegramLocale = getTelegramWebApp()?.initDataUnsafe.user?.language_code?.toLowerCase();
  if (telegramLocale?.startsWith("ru")) return "ru";
  return "ro";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    setLocale: setLocaleState,
    t: translations[locale],
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used within LocaleProvider");
  return context;
}
